import { VENDOR_LOCATIONS } from "src/constants";

export function isInvalidString(data: unknown, minLength: number = 0, maxLength: number = 1200): boolean {
  return !(data && typeof data === "string" && data.length >= minLength && data.length <= maxLength);
}

export function isInvalidPurchaseOrderNumber(data: unknown): boolean {
  return isInvalidString(data, 1) || !(typeof data === "string");
}

export function isInvalidAsn(data: unknown): boolean {
  return isInvalidString(data, 5, 35);
}

export function isInvalidTrackingNumber(data: unknown): boolean {
  return isInvalidString(data, 5, 22) || !(typeof data === "string");
}

export function isInvalidVendorName(data: unknown): boolean {
  const unSupportedCharsRegex = /[\u00BC\u00BD\u00BE\u2150-\u218F\u2E00-\u2E7F\u2044\u0100-\u017F\u0180-\u024F]/g;

  return isInvalidString(data, 2, 55) || unSupportedCharsRegex.test(data as string);
}

export function isInvalidVendorLocation(data: unknown): boolean {
  return isInvalidString(data, 5, 25) || !(typeof data === "string") || !VENDOR_LOCATIONS.includes(data ?? "");
}

export function isInvalidQuantity(data: unknown, min: number = 1, max: number = 600000): boolean {
  const parsedQuantity = Number(data);
  return (
    !data ||
    isNaN(parsedQuantity) ||
    !(typeof data === "string") ||
    data?.includes(".") ||
    !Number.isInteger(parsedQuantity) ||
    parsedQuantity < min ||
    parsedQuantity > max
  );
}

export function isInvalidShipmentCost(data: unknown): boolean {
  return isInvalidCost(data, 0, 200000);
}

export function isInvalidImportDutyCost(data: unknown): boolean {
  return isInvalidCost(data, 0, 200000);
}

export function isInvalidCancellationReason(data: unknown): boolean {
  return isInvalidString(data, 2, 650);
}

export function isInvalidFloat(data: unknown, min: number, max: number, maxDP: number = 10): boolean {
  if (typeof data !== "string") {
    return true;
  }
  const parsedValue = Number(data);
  const decimalPlaces = (data.split(".")[1] ?? "").length;

  return isNaN(parsedValue) || parsedValue < min || parsedValue > max || decimalPlaces > maxDP;
}

export function isInvalidCost(data: unknown, min: number = 1, max: number = 100000): boolean {
  return isInvalidFloat(data, min, max, 2);
}

export function isInvalidEmail(data: unknown): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return isInvalidString(data, 2, 52) || !(typeof data === "string") || !emailRegex.test(data || "");
}

export function isInvalidPassword(data: unknown): boolean {
  if (typeof data !== "string") {
    return true;
  }

  const minLength = 8;
  if (data.length < minLength) {
    return true;
  }
  const hasUpperCase = /[A-Z]/.test(data);
  const hasNumber = /[0-9]/.test(data);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>[\]\\/`~+\-=_]/.test(data);
  return !(hasUpperCase && hasNumber && hasSpecialChar);
}

export function isInvalidOTP(data: unknown): boolean {
  return isInvalidString(data) || !(typeof data === "string") || !/^\d{6}$/.test(data || "");
}

export function isInvalidUnknownArrivalId(data: unknown): boolean {
  const unknownArrivalIdIdRegex = /^UNK-\d{6}$/;
  return !data || !(typeof data === "string") || !unknownArrivalIdIdRegex.test(data);
}

export function isInvalidEmailGreeting(data: unknown): boolean {
  return isInvalidString(data, 2, 20);
}

export function isInvalidUserName(data: unknown): boolean {
  return isInvalidString(data, 3, 30);
}

export function isInvalidProductId(data: unknown): boolean {
  return isInvalidString(data, 5, 15);
}

export function isInvalidProductName(data: unknown): boolean {
  return isInvalidString(data, 2, 200);
}

export function isInvalidProductBarcode(data: unknown): boolean {
  return isInvalidString(data, 2, Number.MAX_SAFE_INTEGER);
}

export function isInvalidWarehouseCheckInId(data: unknown): boolean {
  return isInvalidPurchaseOrderNumber(data) && isInvalidUnknownArrivalId(data);
}

export function isInvalidRole(data: unknown): boolean {
  return isInvalidString(data, 4, 25) || !(typeof data === "string");
}

export function isInvalidFileName(data: unknown): boolean {
  return isInvalidString(data, 2, 60);
}

export function isInvalidShipmentField(data: unknown, maxLength: number = 100): boolean {
  return data !== "" && isInvalidString(data, 0, maxLength);
}

export function isInvalidZipCode(data: unknown): boolean {
  if (!data || typeof data !== "string") return false; // Optional field
  const zipCodeRegex = /^\d{5}(-\d{4})?$/;
  return !zipCodeRegex.test(data);
}

export function isInvalidEIN(data: unknown): boolean {
  if (!data || typeof data !== "string") return false; // Optional field
  const einRegex = /^\d{2}-\d{7}$/;
  return !einRegex.test(data);
}
